import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, isSignInWithEmailLink, signInWithEmailLink, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, sendSignInLinkToEmail } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { Paper, Typography, Button, Box, Divider, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Link, CircularProgress, Alert } from '@mui/material';

// Removed unused action code settings logic

// Helper to robustly decode returnTo
function robustDecodeReturnTo(raw: string | null): string | null {
  let value = raw;
  let count = 0;
  while (value && value.startsWith('%') && count < 3) {
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
      count++;
    } catch {
      break;
    }
  }
  return value;
}

const AuthTest: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<string>('');
  // Removed unused verification state
  const [user, setUser] = useState<any>(null);
  const [sendingEmailLink, setSendingEmailLink] = useState(false);
  const [emailLinkDialogOpen, setEmailLinkDialogOpen] = useState(false);
  const [emailSendError, setEmailSendError] = useState<string | null>(null);
  const [googleSignInError, setGoogleSignInError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('returnTo');
  }, [location.search]);

  const privacyPolicyUrl = useMemo(() => {
    const rawBase =
      process.env.REACT_APP_POLICY_BASE_URL ||
      process.env.REACT_APP_KRULTRA_URL ||
      process.env.REACT_APP_PUBLIC_SITE_URL ||
      'https://krultra.no';
    const base = rawBase.replace(/\/+$/, '');
    return `${base}/policy/privacy`;
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) setStatus('Signed in as ' + u.email);
      // If user is signed in and there's a returnTo param, redirect
      if (u) {
        if (returnTo) {
          navigate(returnTo, { replace: true });
        }
      }
    });
    return unsub;
  }, [navigate, returnTo]);

  // Handle sign-in link in URL
  useEffect(() => {
    const auth = getAuth();
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let emailForSignIn = window.localStorage.getItem('emailForSignIn') || '';
      if (!emailForSignIn) {
        emailForSignIn = window.prompt('Please provide your email for confirmation') || '';
      }
      signInWithEmailLink(auth, emailForSignIn, window.location.href)
        .then((result) => {
          setStatus('Successfully signed in as ' + result.user.email);
          setUser(result.user);
          window.localStorage.removeItem('emailForSignIn');
          // Prefer ?returnTo= param in URL
          const params = new URLSearchParams(window.location.search);
          let returnTo = params.get('returnTo');
          returnTo = robustDecodeReturnTo(returnTo);
          if (returnTo) {
            navigate(returnTo, { replace: true });
          } else {
            // Otherwise fallback to stored path
            const returnPath = localStorage.getItem('authReturnPath');
            if (returnPath) {
              localStorage.removeItem('authReturnPath');
              navigate(returnPath, { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          }
        })
        .catch((error) => {
          setStatus('Error signing in: ' + error.message);
        });
    }
  }, [navigate]);

  const sendEmailLink = async () => {
    if (!email) return;

    setEmailSendError(null);
    setSendingEmailLink(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const rawReturnTo = params.get('returnTo');
      let url = window.location.origin + (window.location.pathname || '/');
      if (rawReturnTo) {
        url += `?returnTo=${encodeURIComponent(rawReturnTo)}`;
      }

      const actionCodeSettings = {
        url,
        handleCodeInApp: true,
      };

      const auth = getAuth();
      await sendSignInLinkToEmail(auth, email.trim(), actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email.trim());
      setEmailLinkDialogOpen(true);
    } catch (err: any) {
      setEmailSendError(err?.message || 'Failed to send sign-in email link.');
    } finally {
      setSendingEmailLink(false);
    }
  };

  const signInWithGoogle = async () => {
    setGoogleSignInError(null);
    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // redirect handled by auth state observer
    } catch (err: any) {
      setGoogleSignInError(err?.message || 'Google sign-in failed.');
    }
  };

  const handleLogout = () => {
    const auth = getAuth();
    auth.signOut();
    setStatus('Logged out');
    setUser(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        p: { xs: 2.5, sm: 4 },
        maxWidth: 560,
        width: '100%',
        mx: 'auto',
        my: 6,
        backgroundColor: '#0B0B0C',
        color: '#FFFFFF',
        border: '1px solid rgba(255,255,255,0.10)',
      }}
    >
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, lineHeight: 1.05, mb: 1.5 }}>
        Sign in to RunnersHub
      </Typography>
      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.72)', mb: 3, fontWeight: 500 }}>
        Sign in or create an account to continue.
      </Typography>
      {/* Show message if redirected here for login-required route */}
      {returnTo ? (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          You need to sign in to continue.
        </Alert>
      ) : null}
      
      {user ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Signed in as: <strong>{user.email}</strong>
          </Typography>
          
          <Button
            variant="contained"
            color="inherit"
            onClick={handleLogout}
            sx={{
              mb: 2,
              backgroundColor: 'black',
              color: 'white',
              border: '2px solid var(--color-surface-border)',
              fontWeight: 700,
              minWidth: 210,
              '&:hover': { backgroundColor: '#222' }
            }}
          >
            Log out
          </Button>
          
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            You are now logged in. Redirecting you back...
          </Typography>
        </Box>
      ) : (
        <>
          {googleSignInError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {googleSignInError}
            </Alert>
          ) : null}
          {emailSendError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emailSendError}
            </Alert>
          ) : null}

          <Button
            fullWidth
            variant="outlined"
            onClick={signInWithGoogle}
            sx={{
              py: 1.4,
              borderRadius: 2,
              borderColor: 'rgba(255,255,255,0.18)',
              color: '#FFFFFF',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.30)',
                backgroundColor: 'rgba(255,255,255,0.06)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ width: 18, height: 18, display: 'inline-flex' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.04 1.53 7.43 2.81l5.43-5.43C33.58 3.88 29.23 2 24 2 14.63 2 6.66 7.38 2.69 15.19l6.34 4.92C10.69 13.16 16.87 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.14 24.55c0-1.61-.14-2.79-.44-4.01H24v7.6h12.78c-.26 2.01-1.67 5.04-4.79 7.07l-.04.26 6.2 4.8.43.04c3.97-3.66 6.26-9.05 6.26-15.76z"/>
                  <path fill="#FBBC05" d="M9.03 28.11A14.56 14.56 0 0 1 8.27 24c0-1.43.26-2.82.73-4.11l-.03-.28-6.38-5.0-.21.1A22 22 0 0 0 2 24c0 3.56.86 6.93 2.38 9.89l6.65-5.78z"/>
                  <path fill="#34A853" d="M24 46c5.23 0 9.63-1.72 12.84-4.69l-6.62-5.13c-1.77 1.23-4.15 2.09-6.22 2.09-7.13 0-13.31-3.66-15.47-8.7l-.26.02-6.53 5.05-.09.25C6.62 40.62 14.63 46 24 46z"/>
                </svg>
              </Box>
              <span>Continue with Google</span>
            </Box>
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2.5 }}>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.12)' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 700, letterSpacing: 1 }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.12)' }} />
          </Box>

          <TextField
            fullWidth
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            InputProps={{
              sx: {
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.06)',
                color: '#FFFFFF',
              },
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.20)' },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.55)', opacity: 1 },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={sendEmailLink}
            disabled={!email || sendingEmailLink}
            sx={{
              py: 1.4,
              borderRadius: 2,
              backgroundColor: '#FFFFFF',
              color: '#000000',
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.90)' },
            }}
          >
            {sendingEmailLink ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: '#000000' }} />
                <span>Sending…</span>
              </Box>
            ) : (
              'Continue with email'
            )}
          </Button>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', mt: 2, display: 'block', lineHeight: 1.5 }}>
            By continuing, you agree to RunnersHub’s{' '}
            <Link href="#" underline="hover" sx={{ color: 'rgba(255,255,255,0.80)' }}>
              Terms
            </Link>
            {' '}and acknowledge the{' '}
            <Link href={privacyPolicyUrl} underline="hover" sx={{ color: 'rgba(255,255,255,0.80)' }} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
            .
          </Typography>

          {status ? (
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.65)' }}>
              {status}
            </Typography>
          ) : null}
        </>
      )}

      <Dialog
        open={emailLinkDialogOpen}
        onClose={() => setEmailLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Check your email</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We sent a secure sign-in link to <strong>{email}</strong>.
          </Typography>

          <Typography variant="body2" paragraph>
            <strong>Important tips:</strong>
          </Typography>
          <ul style={{ paddingLeft: '20px', marginTop: 0, marginBottom: '16px' }}>
            <li>
              <Typography variant="body2">
                Look for an email with the subject <strong>"Sign in to runnershub-62442 requested at [date and time]"</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                The link in the email will say <strong>"Sign in to runnershub-62442"</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                If you don't see it in your inbox, check your spam or junk folder
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                The link in the email is valid for 24 hours
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                It is recommended that you open the link on the same device you requested it from for the most reliable experience
              </Typography>
            </li>
          </ul>

          <Typography variant="body2" paragraph>
            Example email:
          </Typography>
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 1,
              mb: 1,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <img
              src="/images/emailLinkScreenshot.png"
              alt="Example of sign-in email"
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                margin: '0 auto',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEmailLinkDialogOpen(false);
            }}
          >
            Close
          </Button>
          <Button
            onClick={sendEmailLink}
            disabled={!email || sendingEmailLink}
            variant="contained"
          >
            {sendingEmailLink ? 'Sending…' : 'Resend email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AuthTest;
